import type { CatalogRosterRow, GridPlacement, ProjectState } from "@livcg/schema";
import { GRID_COLS, GRID_ROWS } from "@livcg/schema";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { api } from "../../shared/api.js";
import { sendRaw } from "../../shared/sendRaw.js";
import { useMatchDetail } from "../useMatchDetail.js";
import { Button, SegmentedControl, cn, useToast } from "../../ui/index.js";
import { byNumber } from "../rosterOrder.js";
import { SchemeField } from "./SchemeField.js";

type Side = "home" | "away";
type DragState = { playerId: string } | null;
/** The display minimum needed to drop onto the board — both roster and slot are
 *  normalised to this shape. */
type BenchItem = { playerId: string; firstName: string; lastName: string; number: number | null };

const POS_RANK: Record<string, number> = { GK: 0, DEF: 1, MID: 2, FWD: 3 };

/** Starter → position (GK→DEF→MID→FWD) → number: the order "PFL tarkibidan"
 *  feeds the formation slots in (the bench list itself is by number). */
function sortRoster(a: CatalogRosterRow, b: CatalogRosterRow): number {
  return (
    Number(b.isStarter) - Number(a.isStarter) ||
    (POS_RANK[a.position ?? ""] ?? 9) - (POS_RANK[b.position ?? ""] ?? 9) ||
    (a.number ?? 999) - (b.number ?? 999)
  );
}

/** 5×9 placement board — analogue of the SKORE plgrid editor. Chips are dragged
 *  with the pointer; dropping on a cell sends grid.set; an occupied cell
 *  swaps places; outside the board = remove. Double click — captain. */
export function GridPanel({ slug, state }: { slug: string; state: ProjectState }) {
  const { toast } = useToast();
  const [side, setSide] = useState<Side>("home");
  const [drag, setDrag] = useState<DragState>(null);
  /** The cell the dragged player is hovering over ("row:col") — the drop target. */
  const [hover, setHover] = useState<string | null>(null);
  if (!drag && hover !== null) setHover(null);
  // Full match squad (once per matchId) — the bench is fed from it.
  // If it doesn't load, the bench safely falls back to the lineup slots.
  const md = useMatchDetail(state);

  const placements = state.grid[side];
  const byCell = new Map(placements.map((p) => [`${p.row}:${p.col}`, p]));
  const placedIds = new Set(placements.map((p) => p.playerId));

  const roster = (side === "home" ? md.detail?.home.roster : md.detail?.away.roster) ?? [];
  // Bench: the full squad (if loaded), otherwise the lineup slots;
  // those already placed on the board are excluded.
  const benchSource: BenchItem[] =
    roster.length > 0
      ? [...roster]
          .sort((a, b) => Number(b.isStarter) - Number(a.isStarter) || byNumber(a, b))
          .map((r) => ({ playerId: r.playerId, firstName: r.firstName, lastName: r.lastName, number: r.number }))
      : state.lineup.slots
          .filter((s) => s.playerId)
          .map((s) => ({ playerId: s.playerId as string, firstName: s.firstName, lastName: s.lastName, number: s.number }));
  const bench = benchSource.filter((b) => !placedIds.has(b.playerId));

  const commit = async (next: GridPlacement[]) => {
    const res = (await sendRaw("grid.set", { slug, side, placements: next })) as
      | { ok?: boolean; error?: string }
      | undefined;
    if (res && res.ok === false) {
      toast({ tone: "error", title: "Joylab bo'lmadi", description: res.error ?? "" });
    }
  };

  /** drop target: onto cell (row,col) — if occupied, they swap places. */
  const dropTo = (row: number, col: number) => {
    if (!drag) return;
    const next = placements.map((p) => ({ ...p }));
    const occupied = next.find((p) => p.row === row && p.col === col);
    const moving = next.find((p) => p.playerId === drag.playerId);
    if (moving) {
      // A plain click on its own cell — we don't send a redundant grid.set (so the
      // dblclick captain flow is also 1 commit, not 3).
      if (moving.row === row && moving.col === col) {
        setDrag(null);
        return;
      }
      if (occupied && occupied.playerId !== moving.playerId) {
        occupied.row = moving.row;
        occupied.col = moving.col;
      }
      moving.row = row;
      moving.col = col;
      void commit(next);
    } else {
      // Bringing in from the bench — we take the display data from the bench source
      // (roster or lineup slot).
      const slot = state.lineup.slots.find((s) => s.playerId === drag.playerId);
      const src: BenchItem | null =
        benchSource.find((b) => b.playerId === drag.playerId) ??
        (slot?.playerId
          ? { playerId: slot.playerId, firstName: slot.firstName, lastName: slot.lastName, number: slot.number }
          : null);
      if (!src) return;
      if (occupied) {
        // dropped from the bench onto an occupied cell: the existing one leaves the board.
        const filtered = next.filter((p) => p.playerId !== occupied.playerId);
        filtered.push({
          playerId: src.playerId, firstName: src.firstName, lastName: src.lastName,
          number: src.number, captain: false, row, col,
        });
        void commit(filtered);
      } else {
        next.push({
          playerId: src.playerId, firstName: src.firstName, lastName: src.lastName,
          number: src.number, captain: false, row, col,
        });
        void commit(next);
      }
    }
    setDrag(null);
  };

  const removeFromBoard = () => {
    if (!drag) return;
    void commit(placements.filter((p) => p.playerId !== drag.playerId));
    setDrag(null);
  };

  const toggleCaptain = (playerId: string) => {
    void commit(
      placements.map((p) => ({
        ...p,
        captain: p.playerId === playerId ? !p.captain : false, // a single captain
      })),
    );
  };

  const [syncing, setSyncing] = useState(false);
  /** Same as the admin "Yangilash": re-pulls the match squad, crew and coaches
   *  from PFL; the live project keeps score, clock and the operator's colours. */
  const resync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await api.post(`/api/sync/match/${state.matchId.replace(/^m_/, "")}`);
      toast({ tone: "success", title: "Yangilandi", description: "Tarkib PFL'dan qayta yuklandi" });
    } catch (e) {
      toast({ tone: "error", title: "Sync xatosi", description: (e as Error).message });
    } finally {
      setSyncing(false);
    }
  };

  const autoFill = async () => {
    const res = (await sendRaw("grid.autoFill", { slug, side })) as
      | { ok?: boolean; error?: string }
      | undefined;
    if (res && res.ok === false) {
      toast({
        tone: "error",
        title: "Avto-joylash ishlamadi",
        description: res.error === "lineup_empty" ? "Avval Lineup'da o'yinchilarni tanlang" : (res.error ?? ""),
      });
    } else {
      toast({ tone: "success", title: "Formatsiyadan joylandi", description: state.lineup.schemes[side] });
    }
  };

  /** From the PFL squad in one click: catalog roster (starters, in GK→DEF→MID→FWD
   *  order) → lineup slots → from the formation onto the board. The operator does
   *  not have to pick 11 players by hand — in a real project the grid is ready
   *  in an instant with this button. */
  const fillFromRoster = async () => {
    if (roster.length === 0) {
      toast({
        tone: "error",
        title: "Tarkib topilmadi",
        description: md.loading ? "Yuklanmoqda…" : "PFL sync qilinganmi?",
      });
      return;
    }
    const picked = [...roster].sort(sortRoster).slice(0, 11);
    const slots = Array.from({ length: 11 }, (_, i) => {
      const r = picked[i];
      return r
        ? { playerId: r.playerId, firstName: r.firstName, lastName: r.lastName, number: r.number }
        : { playerId: null, firstName: "", lastName: "", number: null };
    });
    const setRes = (await sendRaw("lineup.setSlots", { slug, slots })) as
      | { ok?: boolean; error?: string }
      | undefined;
    if (setRes && setRes.ok === false) {
      toast({ tone: "error", title: "Slotlar yozilmadi", description: setRes.error ?? "" });
      return;
    }
    await autoFill();
  };

  // So an empty pitch doesn't go out if the operator forgets to fill the board and
  // presses Efirga — if the board is empty we first autoFill from the formation, and
  // only then put it on air. If the lineup is empty too (lineup_empty) — error, we don't.
  const showOnAir = async (s: Side) => {
    if (state.grid[s].length === 0) {
      const res = (await sendRaw("grid.autoFill", { slug, side: s })) as
        | { ok?: boolean; error?: string }
        | undefined;
      if (res && res.ok === false) {
        toast({
          tone: "error",
          title: "Efirga uzatib bo'lmadi",
          description:
            res.error === "lineup_empty"
              ? "Avval Lineup'da o'yinchilarni tanlang"
              : (res.error ?? ""),
        });
        return;
      }
    }
    void sendRaw("lineup.show", {
      slug, side: s,
      scheme: state.lineup.schemes[s],
      slots: state.lineup.slots,
    });
  };

  const dragged: BenchItem | null = drag
    ? (placements.find((p) => p.playerId === drag.playerId) ??
      benchSource.find((b) => b.playerId === drag.playerId) ??
      null)
    : null;

  /** The cell's drop-target state: not under the cursor / empty cell / occupied
   *  (they swap places, or the one coming from the bench pushes it out). */
  const targetOf = (key: string, p: GridPlacement | undefined) => {
    if (!drag || hover !== key) return null;
    if (p && p.playerId !== drag.playerId) return "swap" as const;
    return "empty" as const;
  };

  const hoverProps = (key: string) => ({
    onPointerEnter: () => drag && setHover(key),
    onPointerLeave: () => setHover((h) => (h === key ? null : h)),
  });

  const ghost = (b: BenchItem) => (
    <div className="pointer-events-none flex h-full w-full flex-col items-center justify-center rounded text-center leading-none text-accent-strong opacity-70">
      <span className="max-w-full truncate px-0.5 text-[8.5px] font-semibold uppercase">
        {b.lastName || b.firstName}
      </span>
      <span className="text-[13px] font-bold tabular-nums">{b.number ?? "—"}</span>
    </div>
  );

  const cellContent = (p: GridPlacement | undefined, target: "empty" | "swap" | null) => {
    if (target === "empty" && dragged && (!p || p.playerId === drag?.playerId)) return ghost(dragged);
    if (!p) return null;
    return (
      <div className="relative h-full w-full">
        {chip(p)}
        {target === "swap" && (
          <span className="pointer-events-none absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-warning text-[10px] font-bold text-canvas">
            ⇄
          </span>
        )}
      </div>
    );
  };

  const chip = (p: GridPlacement) => (
    <button
      key={p.playerId}
      type="button"
      onPointerDown={(e) => {
        // On touch, implicit pointer-capture returns the pointerup to the chip
        // itself — we release it so the drop lands on the target cell.
        (e.target as Element).releasePointerCapture?.(e.pointerId);
        setDrag({ playerId: p.playerId });
      }}
      onDoubleClick={() => toggleCaptain(p.playerId)}
      title={`${p.lastName || p.firstName} — ikki bosish: sardor`}
      className={cn(
        "flex h-full w-full select-none flex-col items-center justify-center rounded border text-center leading-none",
        "cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60",
        p.row === -1
          ? "border-ready-strong/60 bg-ready-strong/15 text-ink"
          : "border-border-strong bg-surface-elevated text-ink",
        drag?.playerId === p.playerId && "opacity-40",
      )}
    >
      <span className="max-w-full truncate px-0.5 text-[8.5px] font-semibold uppercase">
        {p.lastName || p.firstName}
        {p.captain && <span className="ml-0.5 rounded-full bg-ink px-0.5 text-[7px] text-surface">C</span>}
      </span>
      <span className="text-[13px] font-bold tabular-nums">{p.number ?? "—"}</span>
    </button>
  );

  return (
    <div className="flex flex-col gap-3" onPointerUp={() => setDrag(null)}>
      <div className="flex items-center justify-between gap-2">
        <SegmentedControl<Side>
          options={[{ value: "home", label: "Home" }, { value: "away", label: "Away" }]}
          value={side}
          onChange={setSide}
        />
        <div className="flex items-center gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void resync()}
            disabled={syncing}
            title="Tarkibni PFL'dan qayta yuklash"
            leadingIcon={<RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} />}
          >
            Yangilash
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void fillFromRoster()}>
            PFL tarkibidan
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void autoFill()}>
            Formatsiyadan joylash
          </Button>
          <Button variant="success" size="sm" onClick={() => void showOnAir(side)}>
            Efirga ({side === "home" ? "H" : "A"})
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void sendRaw("lineup.hide", { slug })}>
            Yashirish
          </Button>
        </div>
      </div>

      <div className="w-56">
        <SchemeField
          key={side}
          label={`Formatsiya · ${state.teams[side]?.shortName ?? (side === "home" ? "Home" : "Away")}`}
          value={state.lineup.schemes?.[side] ?? "4-3-3"}
          onChange={(scheme) => void sendRaw("lineup.setScheme", { slug, side, scheme })}
        />
      </div>

      {/* 5×9 board. Attack on top (row 0), defence at the bottom (row 4) — same as the graphic. */}
      <div className="rounded-md border border-divider bg-surface-muted/40 p-2">
        <div
          className="grid gap-1"
          style={{ gridTemplateRows: `repeat(${GRID_ROWS}, 44px)`, gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}
        >
          {Array.from({ length: GRID_ROWS * GRID_COLS }, (_, i) => {
            const row = Math.floor(i / GRID_COLS);
            const col = i % GRID_COLS;
            const key = `${row}:${col}`;
            const p = byCell.get(key);
            const target = targetOf(key, p);
            return (
              <div
                key={i}
                {...hoverProps(key)}
                onPointerUp={(e) => { e.stopPropagation(); dropTo(row, col); }}
                className={cn(
                  "rounded border border-dashed border-divider/60 transition-colors",
                  drag && "border-accent-strong/40 bg-accent/5",
                  target === "empty" && "border-solid border-accent-strong bg-accent/15 ring-2 ring-accent-strong/40",
                  target === "swap" && "border-solid border-warning ring-2 ring-warning/50",
                )}
              >
                {cellContent(p, target)}
              </div>
            );
          })}
        </div>
        {/* GK slot — centered below the board */}
        <div className="mt-1 flex justify-center">
          <div
            {...hoverProps("-1:-1")}
            onPointerUp={(e) => { e.stopPropagation(); dropTo(-1, -1); }}
            className={cn(
              "h-[44px] w-[10.5%] min-w-[64px] rounded border border-dashed border-ready-strong/50 transition-colors",
              drag && "bg-ready-strong/10",
              targetOf("-1:-1", byCell.get("-1:-1")) === "empty" &&
                "border-solid border-ready-strong bg-ready-strong/25 ring-2 ring-ready-strong/40",
              targetOf("-1:-1", byCell.get("-1:-1")) === "swap" && "border-solid border-warning ring-2 ring-warning/50",
            )}
            title="Darvozabon sloti"
          >
            {cellContent(byCell.get("-1:-1"), targetOf("-1:-1", byCell.get("-1:-1")))}
          </div>
        </div>
      </div>

      {/* Bench: those from the full match squad not yet placed on the board —
          they can be dragged into place directly, no lineup selection required */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="t-mono">Joylanmagan:</span>
        {bench.length === 0 && <span className="text-[11px] text-ink-faint">yo'q</span>}
        {bench.map((s) => (
          <button
            key={s.playerId}
            type="button"
            onPointerDown={(e) => {
              (e.target as Element).releasePointerCapture?.(e.pointerId);
              setDrag({ playerId: s.playerId as string });
            }}
            className={cn(
              "cursor-grab select-none rounded-md border border-border bg-surface px-2 py-1 text-[11px] text-ink",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong/60",
              drag?.playerId === s.playerId && "opacity-40",
            )}
          >
            {s.number ? `#${s.number} ` : ""}{s.lastName || s.firstName}
          </button>
        ))}
        {drag && (
          <Button variant="ghost" size="xs" onClick={removeFromBoard} className="text-danger-strong">
            Doskadan olish
          </Button>
        )}
      </div>
      <p className="text-[10.5px] text-ink-faint">
        Chip'ni bosib turib katak ustida qo'yib yuboring · band katak bilan o'rin almashadi · ikki bosish — sardor "C".
      </p>
    </div>
  );
}
